/**
 * SponsorPlanMembers — a health plan's own members at partner orgs (TRUS-768).
 *
 * A plan member is an enrollment at a partner org the sponsor has an active
 * relationship with, whose payer is the sponsor's payer. The backend applies
 * both conditions; this view only shows what it returns.
 * A sponsor with no payer sees an explanation instead of an empty table.
 */
import React, { useEffect, useState } from 'react';
import { Search, Users } from 'lucide-react';

import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { apiClient } from '@/services/apiClient';
import { useAppTranslation } from '@/contexts/TranslationContext';

interface OrgCount {
  cbo_id: number;
  cbo_name: string;
  members: number;
  active_members: number;
}

interface Summary {
  payer_id: number | null;
  payer_name: string | null;
  total_members: number;
  active_members: number;
  orgs: OrgCount[];
}

interface PlanMember {
  enrollment_id: number;
  member_id: number;
  member_number: string | null;
  first_name: string | null;
  last_name: string | null;
  cbo_id: number;
  cbo_name: string;
  program_id: number | null;
  program_name: string | null;
  enrollment_status: string | null;
  enrollment_date: string | null;
}

const PAGE_SIZE = 50;

export function SponsorPlanMembers({ sponsorId }: { sponsorId: number }) {
  const { t } = useAppTranslation();
  const [summary, setSummary] = useState<Summary | null>(null);
  const [members, setMembers] = useState<PlanMember[]>([]);
  const [total, setTotal] = useState(0);
  const [orgFilter, setOrgFilter] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    apiClient
      .get(`/api/sponsors/${sponsorId}/plan-members/summary`)
      .then((r) => !cancelled && setSummary(r.data))
      .catch(() => !cancelled && setError(true));
    return () => {
      cancelled = true;
    };
  }, [sponsorId]);

  // Search waits for a pause in typing.
  useEffect(() => {
    const id = setTimeout(() => {
      setQuery(search.trim());
      setPage(0);
    }, 300);
    return () => clearTimeout(id);
  }, [search]);

  useEffect(() => {
    if (!summary?.payer_id) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(page * PAGE_SIZE) });
    if (orgFilter) params.set('cbo_id', String(orgFilter));
    if (query) params.set('search', query);
    apiClient
      .get(`/api/sponsors/${sponsorId}/plan-members?${params.toString()}`)
      .then((r) => {
        if (cancelled) return;
        setMembers(r.data.items || []);
        setTotal(r.data.total || 0);
      })
      .catch(() => !cancelled && setError(true))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [sponsorId, summary?.payer_id, orgFilter, query, page]);

  if (error) {
    return (
      <Card className="p-6 text-sm text-gray-600 dark:text-gray-300">
        {t('sponsorplanmembers.load_error', 'Plan members could not be loaded. Refresh the page to try again.')}
      </Card>
    );
  }
  if (!summary) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner />
      </div>
    );
  }
  if (!summary.payer_id) {
    return (
      <Card className="p-6 space-y-1">
        <p className="font-medium">{t('sponsorplanmembers.no_payer_title', 'No health plan linked')}</p>
        <p className="text-sm text-gray-600 dark:text-gray-300">
          {t(
            'sponsorplanmembers.no_payer_body',
            'Plan members appear once this sponsor is linked to a payer. Ask a platform admin to link it.',
          )}
        </p>
      </Card>
    );
  }

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-3">
        <Card className="p-4">
          <p className="text-xs uppercase tracking-wide text-gray-500">{t('sponsorplanmembers.plan', 'Health plan')}</p>
          <p className="mt-1 text-lg font-semibold">{summary.payer_name}</p>
        </Card>
        <Card className="p-4">
          <p className="text-xs uppercase tracking-wide text-gray-500">{t('sponsorplanmembers.members', 'Plan members')}</p>
          <p className="mt-1 text-2xl font-semibold tabular-nums">{summary.total_members}</p>
          <p className="text-xs text-gray-500 tabular-nums">
            {t('sponsorplanmembers.active_count', '{{count}} active', { count: summary.active_members })}
          </p>
        </Card>
        <Card className="p-4">
          <p className="text-xs uppercase tracking-wide text-gray-500">{t('sponsorplanmembers.partner_orgs', 'Partner organizations')}</p>
          <p className="mt-1 text-2xl font-semibold tabular-nums">{summary.orgs.length}</p>
        </Card>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => {
            setOrgFilter(null);
            setPage(0);
          }}
          className={`rounded-full border px-3 py-1 text-sm ${orgFilter === null ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-gray-300 text-gray-600'}`}
        >
          {t('sponsorplanmembers.all_orgs', 'All organizations')} · {summary.total_members}
        </button>
        {summary.orgs.map((o) => (
          <button
            key={o.cbo_id}
            type="button"
            onClick={() => {
              setOrgFilter(o.cbo_id);
              setPage(0);
            }}
            className={`rounded-full border px-3 py-1 text-sm ${orgFilter === o.cbo_id ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-gray-300 text-gray-600'}`}
          >
            {o.cbo_name} · {o.members}
          </button>
        ))}
        <div className="relative ml-auto w-full sm:w-64">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
          <Input
            id="plan-member-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('sponsorplanmembers.search_placeholder', 'Name or member number')}
            className="pl-8"
          />
        </div>
      </div>

      <Card className="overflow-x-auto">
        {loading ? (
          <div className="flex justify-center py-10">
            <LoadingSpinner />
          </div>
        ) : members.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-10 text-sm text-gray-500">
            <Users className="h-6 w-6" />
            {t('sponsorplanmembers.empty', 'No plan members match.')}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b text-left text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-3">{t('sponsorplanmembers.col_member', 'Member')}</th>
                <th className="px-4 py-3">{t('sponsorplanmembers.col_member_number', 'Member number')}</th>
                <th className="px-4 py-3">{t('sponsorplanmembers.col_org', 'Organization')}</th>
                <th className="px-4 py-3">{t('sponsorplanmembers.col_program', 'Program')}</th>
                <th className="px-4 py-3">{t('sponsorplanmembers.col_status', 'Status')}</th>
                <th className="px-4 py-3">{t('sponsorplanmembers.col_enrolled', 'Enrolled')}</th>
              </tr>
            </thead>
            <tbody>
              {members.map((m) => (
                <tr key={m.enrollment_id} className="border-b last:border-0">
                  <td className="px-4 py-3 font-medium">{[m.first_name, m.last_name].filter(Boolean).join(' ') || '—'}</td>
                  <td className="px-4 py-3 tabular-nums">{m.member_number || '—'}</td>
                  <td className="px-4 py-3">{m.cbo_name}</td>
                  <td className="px-4 py-3">{m.program_name || '—'}</td>
                  <td className="px-4 py-3">
                    <Badge variant={(m.enrollment_status || '').toLowerCase() === 'active' ? 'default' : 'secondary'}>
                      {m.enrollment_status || '—'}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 tabular-nums">{m.enrollment_date ? m.enrollment_date.slice(0, 10) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {pages > 1 && (
        <div className="flex items-center justify-end gap-3 text-sm">
          <button
            type="button"
            disabled={page === 0}
            onClick={() => setPage((p) => p - 1)}
            className="rounded border px-3 py-1 disabled:opacity-40"
          >
            {t('sponsorplanmembers.previous', 'Previous')}
          </button>
          <span className="tabular-nums text-gray-600">
            {t('sponsorplanmembers.page_of', 'Page {{page}} of {{pages}}', { page: page + 1, pages })}
          </span>
          <button
            type="button"
            disabled={page + 1 >= pages}
            onClick={() => setPage((p) => p + 1)}
            className="rounded border px-3 py-1 disabled:opacity-40"
          >
            {t('sponsorplanmembers.next', 'Next')}
          </button>
        </div>
      )}
    </div>
  );
}

export default SponsorPlanMembers;
